// SIL-717: extracted out of AppShell.tsx. Zero React imports — the icon
// types below come from lucide-react, not React, so this module stays
// import-safe from anywhere without dragging in react-query/lib/api/
// authContext the way importing straight from AppShell.tsx used to.
import {
  LayoutDashboard,
  Package,
  Boxes,
  Tag,
  ArrowLeftRight,
  User,
  ShoppingCart,
  Store,
  FileText,
  HandCoins,
  Send,
  ClipboardList,
  Users,
  MapPin,
  Plug,
  Sparkles,
  Scale,
  Landmark,
  CalendarClock,
  Calculator,
  Leaf,
  Globe,
  ShieldAlert,
  ShieldCheck,
  TrendingUp,
  Recycle,
  Building2,
  Link2,
  Target,
  PackageSearch,
  Coins,
  ScanLine,
} from 'lucide-react';
import { hasOrgFeature, type OrgFeature } from '@siliconx/shared';

export type NavEntry = {
  pillar: string;
  pillarIcon: typeof LayoutDashboard;
  group?: string;
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  adminOnly?: boolean;
  /** SIL-171: shows the opportunities newCount badge next to this item. */
  badge?: 'opportunities';
  /**
   * SIL-1034: single source of truth for "this route requires an org
   * feature" — replaces SIL-1019's `businessOnly: boolean`. REQUIRED (not
   * optional) so a new entry can never silently land ungated by omission —
   * `null` is the explicit "every org type" declaration. Hidden entirely
   * from nav when `hasOrgFeature(organizationType, orgFeature)` is false
   * AND enforced by a root-level redirect (`OrgFeatureGate`, __root.tsx) so
   * a direct URL hit is blocked too, not just the nav entry. Every non-null
   * entry here MUST have a matching `access({ orgFeature, ... })` on the
   * API side (middleware/clientAccess.ts) — asserted by
   * `individual-org-nav.test.ts`'s API-parity test, since this flag is UX
   * only and the API gate is the real enforcement boundary.
   */
  orgFeature: OrgFeature | null;
  /**
   * SIL-994/SIL-1034: destination override for an org type that doesn't
   * unlock this entry's `orgFeature` (e.g. "Settings" points at
   * /individual-profile instead of the business seller_org/buyer_org
   * editor at /profile, which an individual org has no use for —
   * legal_name is NULL, 861). Label/icon/grouping unchanged.
   *
   * SIL-1020: an entry carrying BOTH a non-null `orgFeature` and
   * `fallbackTo` (Settings/`/profile`) is remapped, not hidden —
   * `navForRole` below exempts `fallbackTo` entries from the gate hide-
   * filter, since they already have a safe destination to redirect to
   * instead of disappearing from nav. `orgFeature` on such an entry still
   * marks the underlying route as gated for the shared manifest
   * (org-feature-routes.ts) and the root-level `OrgFeatureGate`
   * (__root.tsx), which matches on the entry's raw `to` and blocks a direct
   * URL hit to it regardless of the nav remap.
   */
  fallbackTo?: string;
};

// SIL-364: pillar-rail nav redesign. Flat array is still the single source
// of truth (per the SIL-307 convention) — each entry now carries `pillar`
// (+ `pillarIcon`, repeated per entry the way `section` used to be) and an
// optional `group` for the one pillar (Exchange) with a second grouping
// level. `groupByPillar` below builds the two-level pillar → group → item
// structure the rail/panel render from; NO URL CHANGES from the prior
// flat-section nav (see the per-item history below).
//
// RUF-540: nav items always render for every org, regardless of seller/buyer
// profile completeness — a side whose profile is incomplete shows a locked
// state (ProfileLockedCard) in place of the page body instead of being
// hidden from nav or redirected away.
// SIL-1305: that "always render" convention now applies only once the
// first-run wizard is done (`onboardingPhase(auth) === 'done'`, lib/
// onboardingPhase.ts) — before that, AppShell's setup rail replaces the nav
// list entirely (and NavCommandPalette lists no routes), so there is no nav
// list for this convention to apply to yet. This array/module is unaffected
// either way; the setup-rail swap lives in AppShell.tsx.
// SIL-542 (W1 of the wayfinder UX-consistency spec, resources/plans/
// wayfinder-ux-consistency-spec.md §2.1): labels + structure rewritten to the
// spec's final tree. NO URL CHANGES here — "labels rename, URLs freeze" is
// house convention (SIL-307). The one URL exception in the spec (§2.3,
// Carbon Planning's /financials/sustainability -> /environmental/
// carbon-planning move) is explicitly deferred to SIL-543 and NOT done here;
// Carbon Planning keeps its existing route and stays in the nav (regrouped
// under Portfolio Intelligence, since its old "Sustainability" heading is
// retired) until that issue ships the tab migration + redirect.
// SIL-2173: a broker (organizationType 'broker') sees only Overview, Exchange
// (the orgFeature:null entries) and Organization > Team/Settings. Every
// other entry is gated by an org feature the broker does not hold
// ('portfolio' = own-hardware inventory, business + individual only), so
// hiding, the root-level redirect (OrgFeatureGate) and the API 403 all come
// from the one hasOrgFeature predicate.
export const CUSTOMER_NAV: NavEntry[] = [
  {
    pillar: 'Overview',
    pillarIcon: LayoutDashboard,
    to: '/dashboard',
    label: 'Overview',
    icon: LayoutDashboard,
    orgFeature: null,
  },

  { pillar: 'Portfolio', pillarIcon: Boxes, to: '/assets', label: 'Assets', icon: Package, orgFeature: 'portfolio' },
  // SIL-1027: lots have no meaning for an individual org (a lot groups
  // multiple assets for listing/offer as a unit — a single-user org has no
  // team/cabinet-scale inventory to group).
  {
    pillar: 'Portfolio',
    pillarIcon: Boxes,
    to: '/lots',
    label: 'Lots',
    icon: Boxes,
    orgFeature: 'lots',
  },

  // SIL-1021: the entire Finance/ERP suite (Financial Position, Opportunity
  // Cost, Asset Financials, Depreciation Schedule, Capitalization Policy) is
  // orgFeature: 'finance' — an individual customer_org has no book value or
  // ERP register to reconcile against (legal_name is NULL, 861). Enforced
  // server-side by `access({ orgFeature: 'finance' })` on every backing
  // route (apps/api/src/routes/client/financials*.ts, financials-export.ts)
  // — see packages/shared/src/dto/org-feature-routes.ts.
  {
    pillar: 'Finance',
    pillarIcon: Landmark,
    group: 'Financial Records',
    to: '/financials/book-vs-fmv',
    label: 'Financial Position',
    icon: Scale,
    orgFeature: 'finance',
  },
  {
    pillar: 'Finance',
    pillarIcon: Landmark,
    group: 'Financial Records',
    to: '/financials/opportunity',
    label: 'Opportunity Cost',
    icon: Scale,
    orgFeature: 'finance',
  },
  // SIL-573 (I2-D1, wayfinder-iter2-spec.md §5, §7-D1): per-asset financial
  // table — acquisition cost/date, book value, depreciation-to-date, current
  // FMV band, book-vs-FMV delta, ERP match status. Same Financial Records
  // group as Financial Position/Opportunity Cost above (spec: "Finance >
  // Financial Records").
  {
    pillar: 'Finance',
    pillarIcon: Landmark,
    group: 'Financial Records',
    to: '/financials/asset-financials',
    label: 'Asset Financials',
    icon: Scale,
    orgFeature: 'finance',
  },
  {
    pillar: 'Finance',
    pillarIcon: Landmark,
    group: 'Schedules & Policies',
    to: '/financials/depreciation',
    label: 'Depreciation Schedule',
    icon: CalendarClock,
    orgFeature: 'finance',
  },
  {
    pillar: 'Finance',
    pillarIcon: Landmark,
    group: 'Schedules & Policies',
    to: '/financials/policy',
    label: 'Capitalization Policy',
    icon: Landmark,
    orgFeature: 'finance',
  },

  {
    pillar: 'Exchange',
    pillarIcon: ArrowLeftRight,
    group: 'Selling',
    to: '/listings',
    label: 'My Listings',
    icon: Tag,
    orgFeature: null,
  },
  // SIL-1027: opt-in-gated unsolicited-offer flow (accept_direct_offer()) —
  // gated business-only like Lots above.
  {
    pillar: 'Exchange',
    pillarIcon: ArrowLeftRight,
    group: 'Selling',
    to: '/opportunities',
    label: 'Buyer Demand',
    icon: Sparkles,
    orgFeature: 'marketplaceOps',
    badge: 'opportunities',
  },
  // SIL-646: idle-asset <-> open-demand match feed (scored join, plans/
  // exchange-mechanics-and-export-control.md §6) — distinct from Buyer
  // Demand above, which is the opt-in-gated accept_direct_offer() flow.
  // SIL-1027: also business-only.
  {
    pillar: 'Exchange',
    pillarIcon: ArrowLeftRight,
    group: 'Selling',
    to: '/matches',
    label: 'Asset Matches',
    icon: Target,
    orgFeature: 'marketplaceOps',
  },
  {
    pillar: 'Exchange',
    pillarIcon: ArrowLeftRight,
    group: 'Buying',
    to: '/marketplace',
    label: 'Browse Listings',
    icon: Store,
    orgFeature: null,
  },
  {
    pillar: 'Exchange',
    pillarIcon: ArrowLeftRight,
    group: 'Buying',
    to: '/offers',
    label: 'Offers',
    icon: HandCoins,
    orgFeature: null,
  },
  {
    pillar: 'Exchange',
    pillarIcon: ArrowLeftRight,
    group: 'Buying',
    to: '/direct-offers',
    label: 'Direct Offers',
    icon: Send,
    orgFeature: null,
  },
  // SIL-1027: business-only — the anonymized direct-offer catalog this page
  // browses (catalog.ts) has no meaning for an individual org's single-unit
  // scale of buying.
  {
    pillar: 'Exchange',
    pillarIcon: ArrowLeftRight,
    group: 'Buying',
    to: '/inventory',
    label: 'Market Inventory',
    icon: ShoppingCart,
    orgFeature: 'marketplaceOps',
  },
  // SIL-542: Transactions moves out of Selling into its own Activity group —
  // it isn't a selling action, it's the record of everything in motion.
  {
    pillar: 'Exchange',
    pillarIcon: ArrowLeftRight,
    group: 'Activity',
    to: '/transactions',
    label: 'Transactions',
    icon: ArrowLeftRight,
    orgFeature: null,
  },

  // SIL-307: Scenarios is grouped under Intelligence but its URL stays at
  // /financials/scenarios (unchanged since SIL-252) — a deliberate
  // presentation-only trade-off, NOT a route move. Do not "fix" this into
  // /intelligence/scenarios; that would 404 every existing bookmark/deep
  // link into the scenario builder.
  // SIL-1022: business-org-only — an individual customer_org has no
  // team/board to route a scenario approval through. See
  // NavEntry.orgFeature's docstring.
  {
    pillar: 'Intelligence',
    pillarIcon: Sparkles,
    group: 'Decision Workflows',
    to: '/financials/scenarios',
    label: 'Scenarios',
    icon: Calculator,
    orgFeature: 'scenarios',
  },
  // SIL-1071: purchase-decision section — an org's house rates / cost
  // constants + (in a follow-up issue, SIL-1072) saved purchase analyses.
  // Business-org-only: an individual customer_org has no house-rate/cost-
  // structure concept to model a bid against. API gate is GET/PUT
  // /api/client/buyer-cost-constants (buyer-cost-constants.ts),
  // orgFeature: 'marketIntel'.
  {
    pillar: 'Intelligence',
    pillarIcon: Sparkles,
    group: 'Decision Workflows',
    to: '/market-intelligence',
    label: 'Market Intelligence',
    icon: Coins,
    orgFeature: 'marketIntel',
  },
  {
    pillar: 'Intelligence',
    pillarIcon: Sparkles,
    group: 'Portfolio Intelligence',
    to: '/market-trends',
    label: 'Market Outlook',
    icon: TrendingUp,
    orgFeature: 'portfolio',
  },
  // SIL-472: Environmental — the historical/actuals carbon footprint
  // dashboard (Overview/Fleet detail/Circularity).
  // SIL-1023: business-org-only — API gate is GET /api/client/environmental/
  // overview + /fleet-detail (environmental.ts), orgFeature: 'sustainability'.
  {
    pillar: 'Intelligence',
    pillarIcon: Sparkles,
    group: 'Portfolio Intelligence',
    to: '/environmental',
    label: 'Environmental Impact',
    icon: Globe,
    orgFeature: 'sustainability',
  },
  // SIL-442 (W4, spec §6): the forward-looking do-nothing baseline. Its URL
  // stays under /financials/* for the same SIL-307 reason above — nav
  // placement is presentation, NOT a route move. SIL-496's "Sustainability"
  // group heading is retired by SIL-542; Carbon Planning moves here
  // (Portfolio Intelligence) as a plain rename-and-regroup. Its removal from
  // the rail entirely (becoming an Environmental tab) is spec §2.3 / SIL-543,
  // gated on legal sign-off — NOT done by this issue.
  // SIL-1023: business-org-only — API gate is the carbon-target CRUD +
  // trajectory routes (sustainability.ts's /targets*, sustainability-
  // trajectory.ts's /trajectory), orgFeature: 'sustainability'. NOT /sustainability/config
  // or /grid-intensity/buy-catalog on the same mount — those are shared with
  // the Setup Guide (/financials/setup) and Scenarios' carbon forecast,
  // neither of which is in this issue's scope.
  {
    pillar: 'Intelligence',
    pillarIcon: Sparkles,
    group: 'Portfolio Intelligence',
    to: '/financials/sustainability',
    label: 'Carbon Planning',
    icon: Leaf,
    orgFeature: 'sustainability',
  },
  // SIL-576 (I2-D4): Circularity promoted from an Environmental tab to its
  // own nav entry, following the SIL-542 ESG Reports precedent — URL
  // unchanged (still /environmental/circularity, nav placement is
  // presentation-only).
  // SIL-1023: business-org-only — API gate is GET /api/client/environmental/
  // circularity (environmental.ts), orgFeature: 'sustainability'.
  {
    pillar: 'Intelligence',
    pillarIcon: Sparkles,
    group: 'Portfolio Intelligence',
    to: '/environmental/circularity',
    label: 'Circularity Outcomes',
    icon: Recycle,
    orgFeature: 'sustainability',
  },
  // SIL-542: ESG Reports promoted from a tab on /environmental to its own
  // nav entry — URL unchanged (spec §2.1).
  // SIL-1023: business-org-only — API gate is GET /api/client/environmental/
  // reports, POST /reports/package, GET /methodology.csv (environmental.ts),
  // orgFeature: 'sustainability'.
  {
    pillar: 'Intelligence',
    pillarIcon: Sparkles,
    group: 'Outcomes & Reports',
    to: '/environmental/reports',
    label: 'ESG Reports',
    icon: FileText,
    orgFeature: 'sustainability',
  },
  // SIL-575 (I2-D3): fleet-wide registry over the per-asset digital
  // passports that already exist (passport.$fingerprint.tsx) — a read
  // model, sibling of ESG Reports in the same Outcomes & Reports group.
  // SIL-1027: the REGISTRY is business-only — the per-asset passport
  // (asset detail > passport) stays available to individuals; only this
  // fleet-wide rollup is gated.
  {
    pillar: 'Intelligence',
    pillarIcon: Sparkles,
    group: 'Outcomes & Reports',
    to: '/passports',
    label: 'Digital Passports',
    icon: ShieldCheck,
    orgFeature: 'marketplaceOps',
  },
  // SIL-574 (I2-D2): registry over EXISTING custody/audit records
  // (asset_ownership_event transfers, asset_lifecycle_event advances) — same
  // Outcomes & Reports group as ESG Reports / Digital Passports above.
  // SIL-1028: orgFeature: 'custody' — see NavEntry.orgFeature's docstring
  // and the matching API gate (ORG_FEATURE_API_ROUTES.custody).
  {
    pillar: 'Intelligence',
    pillarIcon: Sparkles,
    group: 'Outcomes & Reports',
    to: '/custody-audit-records',
    label: 'Custody & Audit Records',
    icon: FileText,
    orgFeature: 'custody',
  },

  // SIL-1024: Data Health is orgFeature: 'fleetData' — see
  // NavEntry.orgFeature's docstring and the matching API gate
  // (packages/shared's ORG_FEATURE_API_ROUTES.fleetData).
  {
    pillar: 'Organization',
    pillarIcon: Building2,
    group: 'Data & Integrations',
    to: '/data-health',
    label: 'Data Health',
    icon: ShieldAlert,
    orgFeature: 'fleetData',
  },
  // RUF-378: Integrations is visible to members too (read-only status);
  // connect/disconnect controls inside the page are admin-gated.
  // SIL-1024: orgFeature: 'fleetData' — see NavEntry.orgFeature's docstring
  // and the matching API gate (ORG_FEATURE_API_ROUTES.fleetData).
  {
    pillar: 'Organization',
    pillarIcon: Building2,
    group: 'Data & Integrations',
    to: '/integrations',
    label: 'Integrations',
    icon: Plug,
    orgFeature: 'fleetData',
  },
  {
    pillar: 'Organization',
    pillarIcon: Building2,
    group: 'Data & Integrations',
    to: '/submissions',
    label: 'Submissions',
    icon: ClipboardList,
    orgFeature: 'portfolio',
  },
  // SIL-601: visible to members too (view org link, list/generate/revoke
  // asset links are any-member per clientAccess.ts) — rotate is the only
  // admin-gated action, gated inside the page, same posture as Integrations.
  // SIL-1025: also orgFeature: 'verificationLinks' — the page's org-wide link has no meaning
  // for an individual org (no org-wide identity to publish a link for); its
  // per-asset "Verify this asset" links stay available elsewhere (asset
  // detail page, routes/client/verification.ts) and are NOT gated.
  {
    pillar: 'Organization',
    pillarIcon: Building2,
    group: 'Data & Integrations',
    to: '/verification-links',
    label: 'Verification Links',
    icon: Link2,
    orgFeature: 'verificationLinks',
  },
  // SIL-695: same posture as Verification Links above — visible to members
  // (the Customs list and both triage actions are orgRole 'member' in
  // clientAccess.ts), with the admin-only token actions gated inside the page.
  {
    pillar: 'Organization',
    pillarIcon: Building2,
    group: 'Data & Integrations',
    to: '/customs',
    label: 'Customs',
    icon: PackageSearch,
    orgFeature: 'portfolio',
  },
  // Bench suite: bench runs are visible to every member (list/detail and
  // cosmetic grade are orgRole 'member' in clientAccess.ts); the wipe
  // authorization is admin-gated inside the page, same posture as Customs.
  {
    pillar: 'Organization',
    pillarIcon: Building2,
    group: 'Data & Integrations',
    to: '/bench/runs',
    label: 'Bench Runs',
    icon: ScanLine,
    orgFeature: 'portfolio',
  },
  // RUF-380: Team/Locations admin only. SIL-1019/SIL-1020: Team and
  // Locations are also hidden for an individual org — see
  // NavEntry.orgFeature's docstring. An individual customer_org has no
  // data-center/seller-facility concept to manage here.
  {
    pillar: 'Organization',
    pillarIcon: Building2,
    group: 'Workspace Settings',
    to: '/team',
    label: 'Team',
    icon: Users,
    orgFeature: 'teams',
    adminOnly: true,
  },
  {
    pillar: 'Organization',
    pillarIcon: Building2,
    group: 'Workspace Settings',
    to: '/locations',
    label: 'Locations',
    icon: MapPin,
    orgFeature: 'locations',
    adminOnly: true,
  },
  // SIL-307: "Settings" reuses the existing org profile page (/profile) —
  // there is no dedicated /settings route. SIL-994: an individual org has no
  // seller_org/buyer_org identity to edit there, so it goes to the personal
  // profile editor (/individual-profile, SIL-993) instead — see
  // NavEntry.fallbackTo's docstring. SIL-1020: also marked `orgFeature:
  // 'orgProfile'` (branding/PUE/seller-buyer-org forms are gated on the API
  // side too, org-feature-routes.ts) — `fallbackTo` above means this entry
  // is remapped rather than hidden for an org that doesn't unlock it
  // (navForRole below).
  {
    pillar: 'Organization',
    pillarIcon: Building2,
    group: 'Workspace Settings',
    to: '/profile',
    label: 'Settings',
    icon: User,
    orgFeature: 'orgProfile',
    fallbackTo: '/individual-profile',
  },
];

/**
 * SIL-1021/SIL-1034: `orgFeature`-gated routes with no `CUSTOMER_NAV` entry
 * of their own — the Finance setup guide (`/financials/setup`, reached only
 * via the bare `/financials` redirect and the header's SetupProgressChip,
 * never a nav link) and ERP import triage (`/financials/triage/$batchId`,
 * reached only from the Integrations import wizard), plus the bulk-CSV-
 * import onboarding wizard (`/onboarding`, reached via a dashboard CTA / the
 * "Add asset > CSV" continue button, not a sidebar entry). `OrgFeatureGate`
 * (__root.tsx) keys its normal lookup off `CUSTOMER_NAV` entries via
 * `ORG_FEATURE_ROUTE_PREFIXES` below, which finds nothing for any of
 * these three — this list is the backstop, folded into
 * `ORG_FEATURE_ROUTE_PREFIXES`. Prefix match (not exact) because triage's
 * real path carries a `$batchId` segment.
 */
const NON_NAV_ORG_FEATURE_ROUTES: readonly { prefix: string; feature: OrgFeature }[] = [
  { prefix: '/financials/setup', feature: 'finance' },
  { prefix: '/financials/triage', feature: 'finance' },
  { prefix: '/onboarding', feature: 'onboarding' },
];

/**
 * SIL-1034: single prefix list backing `orgFeatureForPath` below, replacing
 * SIL-1021's `BUSINESS_ONLY_ROUTE_PREFIXES`/`isBusinessOnlyPath` and
 * SIL-1026's `NON_NAV_BUSINESS_ONLY_ROUTES`. Built from `CUSTOMER_NAV`
 * itself (every entry with a non-null `orgFeature`, keyed by its raw `to` —
 * NOT the `fallbackTo` remap, so `/profile` still gates even though an
 * individual org's Settings nav item points at `/individual-profile`) plus
 * the non-nav routes above. Sorted longest-prefix-first so a more specific
 * route (e.g. `/financials/triage`) is never shadowed by a shorter sibling
 * prefix registered earlier.
 */
export const ORG_FEATURE_ROUTE_PREFIXES: readonly { prefix: string; feature: OrgFeature }[] = [
  ...CUSTOMER_NAV.flatMap((e) => (e.orgFeature ? [{ prefix: e.to, feature: e.orgFeature }] : [])),
  ...NON_NAV_ORG_FEATURE_ROUTES,
].sort((a, b) => b.prefix.length - a.prefix.length);

/**
 * The `OrgFeature` gating `pathname`, or `null` if it isn't gated at all.
 * Segment-boundary match (`/lots-foo`, `/lotsx`, `/profile-x`,
 * `/individual-profile` do NOT match `/lots`/`/profile`) with trailing-slash
 * support, case-insensitive (TanStack Router route matching is
 * case-insensitive too). This is UX only — `access()` (middleware/
 * clientAccess.ts) is the real enforcement boundary; this only drives the
 * root-level redirect (`OrgFeatureGate`, __root.tsx) and nav hiding.
 *
 * SIL-1040: decode before lowercasing, so a `%2F`-encoded path (e.g.
 * `/profile%2Fteam`) resolves against the same prefix list as its unencoded
 * form. `decodeURIComponent` throws on a malformed escape sequence (e.g. a
 * lone `%`) — caught, falling back to the raw pathname, since this is UX
 * only and must never throw into the render path.
 */
export function orgFeatureForPath(pathname: string): OrgFeature | null {
  let decoded: string;
  try {
    decoded = decodeURIComponent(pathname);
  } catch {
    decoded = pathname;
  }
  const p = decoded.toLowerCase();
  return (
    ORG_FEATURE_ROUTE_PREFIXES.find(({ prefix }) => p === prefix || p.startsWith(`${prefix}/`))
      ?.feature ?? null
  );
}

/**
 * SIL-1812: true when `pathname` is one of the `pillar: 'Exchange'` entries in
 * `CUSTOMER_NAV` (buy/sell surfaces). Derived from the nav, no hand-kept path
 * list. Same segment-boundary, case-insensitive, decode-safe matching as
 * `orgFeatureForPath`. Backs the individual-profile gate: an individual with an
 * incomplete profile is only sent to /individual-profile from these paths.
 */
export function isExchangePath(pathname: string): boolean {
  let decoded: string;
  try {
    decoded = decodeURIComponent(pathname);
  } catch {
    decoded = pathname;
  }
  const p = decoded.toLowerCase();
  return CUSTOMER_NAV.some(
    (e) => e.pillar === 'Exchange' && (p === e.to || p.startsWith(`${e.to}/`)),
  );
}

export type NavGroup = { heading: string | null; items: NavEntry[] };
export type NavPillar = {
  pillar: string;
  pillarIcon: typeof LayoutDashboard;
  items: NavEntry[];
  groups: NavGroup[];
};

// Two-level pillar → group? → item grouping, in order of first appearance
// in CUSTOMER_NAV (the array itself stays the single ordered source of
// truth — no separate ordering list to keep in sync). Replaces the old
// one-level groupBySection.
export function groupByPillar(entries: NavEntry[]): NavPillar[] {
  const pillarOrder: string[] = [];
  const byPillar = new Map<string, NavEntry[]>();
  for (const entry of entries) {
    if (!byPillar.has(entry.pillar)) {
      pillarOrder.push(entry.pillar);
      byPillar.set(entry.pillar, []);
    }
    byPillar.get(entry.pillar)!.push(entry);
  }
  return pillarOrder.map((pillar) => {
    const items = byPillar.get(pillar)!;
    const groupOrder: (string | null)[] = [];
    const byGroup = new Map<string | null, NavEntry[]>();
    for (const item of items) {
      const heading = item.group ?? null;
      if (!byGroup.has(heading)) {
        groupOrder.push(heading);
        byGroup.set(heading, []);
      }
      byGroup.get(heading)!.push(item);
    }
    return {
      pillar,
      pillarIcon: items[0]!.pillarIcon,
      items,
      groups: groupOrder.map((heading) => ({ heading, items: byGroup.get(heading)! })),
    };
  });
}

/**
 * SIL-495: the pillar owning `pathname`, by LONGEST matching nav prefix.
 *
 * Was an exact `item.to === pathname` match, which left `activePillar`
 * undefined on every child route with no nav entry of its own
 * (`/environmental/circularity`, `/assets/$assetId`,
 * `/financials/scenarios/$id`, ...) — and an undefined pillar unmounts the
 * expanded side panel mid-navigation.
 *
 * Longest, not first: `/financials/*` entries (Depreciation, Scenarios,
 * Carbon Planning) would otherwise all be shadowed by whichever `/financials`
 * prefix appeared first in CUSTOMER_NAV.
 */
export function findActivePillar<P extends { items: { to: string }[] }>(
  pillars: P[],
  pathname: string,
): P | undefined {
  // The Fingerprint-keyed passport page (/passport/$fingerprint) has no nav
  // entry and is not a prefix of one (`/passport` is not `/passports`); it
  // used to live under /assets/$assetId/passport and kept the Assets pillar,
  // so keep resolving it there or the side panel unmounts on that page.
  if (pathname === '/passport' || pathname.startsWith('/passport/')) pathname = '/assets';
  let best: P | undefined;
  let bestLength = -1;
  for (const pillar of pillars) {
    for (const item of pillar.items) {
      const matches = pathname === item.to || pathname.startsWith(`${item.to}/`);
      if (matches && item.to.length > bestLength) {
        best = pillar;
        bestLength = item.to.length;
      }
    }
  }
  return best;
}

// SIL-717: pulled out of AppShell's inline useMemo filter. Deliberately
// narrow — `isAdmin: boolean`, not the whole `user` — per RUF-540: only
// admin-only self-service items (Team, Locations) are filtered from nav;
// every seller/buyer surface always renders regardless of profile
// completeness (an incomplete profile shows a locked state on the page
// itself, not a hidden nav item). Taking the whole `user` here would invite
// someone to add `canSell`/`canBuy` filtering later and silently reverse
// that deliberate decision — keep the narrowness in the signature.
//
// SIL-994/SIL-1019/SIL-1020/SIL-1034: `organizationType` is the one other
// axis allowed here (still not canSell/canBuy) — it drops `orgFeature`-gated
// entries the org type doesn't unlock (Team, Locations for an individual)
// and applies each entry's `fallbackTo` override (Settings ->
// /individual-profile). BOTH params are now REQUIRED (SIL-1034 plan §6.2) —
// `organizationType: null` is the explicit "identity not resolved yet"
// state, and every gated entry is hidden while it's null (see the `pending`
// branch below and this ticket's plan §7 item 5: nav entries wait for /me
// to resolve, rather than defaulting to "show everything" during loading
// the way the old optional-param signature did).
//
// SIL-1020: an entry carrying BOTH a non-null `orgFeature` and `fallbackTo`
// (Settings) is exempted from the hide-filter — it already has a safe
// destination to redirect to (applied by the `.map` below), so outright
// hiding it would regress SIL-994's "Settings still shows, pointed at
// /individual-profile" behavior. An entry with a non-null `orgFeature` and
// NO `fallbackTo` (Team, Locations) has no such substitute and is hidden
// whenever `hasOrgFeature` is false (including while `organizationType` is
// still `null`/loading).
export function navForRole(
  isAdmin: boolean,
  organizationType: 'business' | 'individual' | 'broker' | null,
): NavEntry[] {
  return CUSTOMER_NAV.filter((item) => !item.adminOnly || isAdmin)
    .filter(
      (item) => item.orgFeature === null || hasOrgFeature(organizationType, item.orgFeature) || !!item.fallbackTo,
    )
    .map((item) =>
      // Stay at the raw `to` while organizationType is still unresolved
      // (`null`, loading) — matches the pre-SIL-1034 Settings loading
      // behavior; remap to `fallbackTo` only once identity resolves AND the
      // org type genuinely doesn't unlock the feature.
      item.orgFeature !== null &&
      item.fallbackTo &&
      organizationType !== null &&
      !hasOrgFeature(organizationType, item.orgFeature)
        ? { ...item, to: item.fallbackTo }
        : item,
    );
}
